import React from "react";

/**
 * IconButton — Warm Precision design-system primitive for icon-only buttons.
 *
 * Use for: close X, kebab/overflow menu trigger, expand/collapse arrows, row
 * actions, toolbar icons. Not for: labelled buttons (use <PrimaryButton>,
 * <SecondaryButton>, <GhostButton>) — labels carry meaning that icons alone
 * cannot.
 *
 * Accessibility:
 *   - `aria-label` is REQUIRED. TS will not let you omit it.
 *   - Focus ring uses var(--ds-primary) for brand consistency.
 *   - Disabled state respected via the native `disabled` attribute.
 *
 * Pattern:
 *   <IconButton aria-label="Close" onClick={close}><X className="w-4 h-4" /></IconButton>
 *   <IconButton variant="ghost" aria-label="More"><MoreVertical /></IconButton>
 *   <IconButton variant="destructive" aria-label="Delete row"><Trash /></IconButton>
 */

type Variant = "default" | "ghost" | "destructive";
type Size = "sm" | "md";

interface Props extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label"
> {
  "aria-label": string;
  variant?: Variant;
  size?: Size;
  children: React.ReactNode;
}

const SIZE: Record<Size, string> = {
  sm: "w-7 h-7",
  md: "w-8 h-8"
};

const VARIANT: Record<Variant, string> = {
  default: "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
  ghost: "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800",
  destructive: "text-red-600 hover:bg-red-50"
};

const IconButton = React.forwardRef<HTMLButtonElement, Props>(
  function IconButton(
    {
      variant = "default",
      size = "md",
      className = "",
      children,
      type,
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type || "button"}
        className={`inline-flex items-center justify-center rounded-md transition-colors ${SIZE[size]} ${VARIANT[variant]} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-(--ds-primary) disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

export default IconButton;
